import { getAgentSession } from "@/modules/sessions/requests";
import { AgentSessionStep, AgentSessionStepType } from "@/modules/sessions/types";

type ControlsStepType = AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING | AgentSessionStepType.CONTROL_SELECTION;

// Same margin checkAndRefreshUrl uses before it tries to re-sign a URL.
const MIN_VALIDITY_MS = 5 * 60 * 1000;

const getStepUrl = (step: AgentSessionStep | undefined): string | null | undefined =>
  (step?.data as { url?: string | null } | null | undefined)?.url;

const isExpiredSignedUrl = (url: string): boolean => {
  const expiry = new URL(url).searchParams.get("se");
  return expiry !== null && new Date(expiry).getTime() - Date.now() < MIN_VALIDITY_MS;
};

/**
 * The client cannot re-sign an expired controls URL, but the session read does,
 * so reload the session and return that step with its freshly signed URL.
 */
export const fetchFreshControlsStep = async (
  sessionId: string,
  controlsStepType: ControlsStepType,
): Promise<AgentSessionStep & { data: { url: string } }> => {
  const response = await getAgentSession(sessionId);
  const freshStep = response?.data?.steps?.find((step) => step.type === controlsStepType);
  const freshUrl = getStepUrl(freshStep);
  if (!freshStep || !freshUrl) {
    throw new Error(`Session ${sessionId} returned no controls file URL for ${controlsStepType}`);
  }
  if (isExpiredSignedUrl(freshUrl)) {
    throw new Error(`Session ${sessionId} returned an expired controls file URL for ${controlsStepType}`);
  }
  return freshStep as AgentSessionStep & { data: { url: string } };
};

/**
 * Reassess reopens a completed controls step whose stored URL was signed when the
 * assessment ran, so it has usually expired. Take that step's fresh data instead.
 */
export const withFreshControlsUrl = async (
  sessionId: string,
  steps: AgentSessionStep[],
  controlsStepType: ControlsStepType,
): Promise<AgentSessionStep[]> => {
  const freshStep = await fetchFreshControlsStep(sessionId, controlsStepType);
  return steps.map((step) =>
    step.type === controlsStepType ? ({ ...step, data: freshStep.data } as AgentSessionStep) : step,
  );
};
